import type { TranslationResources, Language } from '../types';

const APP_LANGUAGE_KEY = 'app_language';
const TRANSLATIONS_PREFIX = 'translations_';
const AVAILABLE_LANGS_KEY = 'app_available_langs';

/**
 * Persistencia en localStorage para internacionalización
 */
export const languageStorage = {
  /**
   * Guarda el idioma activo en localStorage
   */
  setLanguage: (lang: string): void => {
    localStorage.setItem(APP_LANGUAGE_KEY, lang);
  },

  /**
   * Obtiene el idioma guardado o null
   */
  getLanguage: (): string | null => {
    return localStorage.getItem(APP_LANGUAGE_KEY);
  },

  /**
   * Guarda las traducciones de un idioma en localStorage
   */
  setTranslations: (lang: string, resources: TranslationResources): void => {
    localStorage.setItem(`${TRANSLATIONS_PREFIX}${lang}`, JSON.stringify(resources));
  },

  /**
   * Obtiene las traducciones cacheadas o null
   */
  getTranslations: (lang: string): TranslationResources | null => {
    const data = localStorage.getItem(`${TRANSLATIONS_PREFIX}${lang}`);
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  },

  /**
   * Elimina las traducciones cacheadas de un idioma concreto.
   * Usado por la invalidación explícita (`refreshTranslations`) para forzar
   * que la siguiente carga venga del backend y no de la caché.
   */
  removeTranslations: (lang: string): void => {
    localStorage.removeItem(`${TRANSLATIONS_PREFIX}${lang}`);
  },

  /**
   * Guarda la lista de idiomas disponibles
   */
  setAvailableLanguages: (languages: Language[]): void => {
    localStorage.setItem(AVAILABLE_LANGS_KEY, JSON.stringify(languages));
  },

  /**
   * Obtiene los idiomas disponibles cacheados
   */
  getAvailableLanguages: (): Language[] | null => {
    const data = localStorage.getItem(AVAILABLE_LANGS_KEY);
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  },

  /**
   * Limpia toda la cache de traducciones
   */
  clearCache: (): void => {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(TRANSLATIONS_PREFIX) || key === AVAILABLE_LANGS_KEY) {
        localStorage.removeItem(key);
      }
    });
  }
};
